import axios from 'axios';
import { authService } from './authService';
import { indexedDbService, ChangeType } from './indexedDbService';
import { offlineService } from './offlineService';
import { apiHealthService } from './apiHealthService';

const API_URL = process.env.REACT_APP_API_URL || `${apiHealthService.getApiUrl()}/api`;

export const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    transformRequest: [
        (data, headers) => {
            // If it's FormData, don't transform it - let browser handle it
            if (data instanceof FormData) {
                // Remove the Content-Type header to let the browser set it with boundary
                delete headers['Content-Type'];
                return data;
            }
            // For all other data, use default JSON transformation
            if (data && typeof data === 'object') {
                return JSON.stringify(data);
            }
            return data;
        }
    ]
});

/**
 * Normalize dates in an object to ensure consistent timezone handling
 * This converts Date objects to ISO strings to preserve timezone information
 */
function normalizeDates(obj: any): any {
    if (!obj) return obj;
    
    // Create a deep copy to avoid modifying the original object
    const result = JSON.parse(JSON.stringify(obj));
    
    // Get the current timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('Normalizing dates with timezone:', timeZone);
    
    // Ensure timeZoneId is set only for requests that need it (appointments, etc.)
    if (typeof result === 'object' && result !== null) {
        if (!result.timeZoneId && typeof obj.timeZoneId === 'string') {
            result.timeZoneId = obj.timeZoneId;
        } else if (!result.timeZoneId && typeof obj === 'object' && obj !== null) {
            // Only add timeZoneId for specific entities that need it
            const needsTimeZone = obj.title || obj.appointmentTypeId || obj.startTime || obj.endTime;
            if (needsTimeZone) {
                result.timeZoneId = timeZone;
                console.log('Setting timeZoneId for time-sensitive request:', timeZone);
            }
        }
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
        return obj.toISOString();
    }
    
    // Process object properties
    if (typeof obj === 'object' && obj !== null) {
        for (const key in obj) {
            if (obj[key] instanceof Date) {
                result[key] = obj[key].toISOString();
                console.log(`Normalized date for ${key}:`, {
                    before: obj[key],
                    after: result[key]
                });
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                result[key] = normalizeDates(obj[key]);
            }
        }
        
        // Special handling for appointment data
        if (result.startTime && result.endTime) {
            console.log('API request appointment time data:', {
                startTime: result.startTime,
                endTime: result.endTime,
                timeZoneId: result.timeZoneId
            });
        }
        
        // Special handling for originalDate
        if (result.originalDate) {
            console.log('API request originalDate:', {
                originalDate: result.originalDate,
                timeZoneId: result.timeZoneId
            });
        }
    }
    
    return result;
}

// Add request interceptor to include the JWT token
apiClient.interceptors.request.use(
    async (config) => {
        const token = authService.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Check if we're offline - use both the service and the browser's status
        const serviceOfflineStatus = !offlineService.getOnlineStatus();
        const browserOfflineStatus = !navigator.onLine;
        const isOffline = serviceOfflineStatus || browserOfflineStatus;
        
        const isMutation = ['post', 'put', 'delete'].includes(config.method?.toLowerCase() || '');
        const isFormData = config.data instanceof FormData;
        
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            serviceOfflineStatus,
            browserOfflineStatus,
            isOffline,
            isMutation,
            isFormData,
            url: config.url,
            contentType: config.headers['Content-Type']
        });

        // Normalize dates in the request data to ensure consistent timezone handling
        // Skip normalization for FormData objects (used for file uploads)
        if (config.data && !isFormData) {
            config.data = normalizeDates(config.data);
        }

        // Skip offline handling for file uploads
        if (isOffline && !isFormData) {
            if (isMutation) {
                // Store the request for later processing
                const entityType = getEntityTypeFromUrl(config.url || '');
                const changeType = getChangeTypeFromMethod(config.method || '');
                
                console.log('Processing offline mutation request:', {
                    entityType,
                    changeType,
                    url: config.url
                });
                
                if (entityType && changeType) {
                    // Extract the entity ID from the URL if it's an update or delete
                    const entityId = getEntityIdFromUrl(config.url || '', config.method || '');
                    
                    // Store the request in IndexedDB
                    await indexedDbService.addPendingChange({
                        entityId: entityId || 'new',
                        entityType,
                        changeType,
                        data: config.data,
                        endpoint: config.url || '',
                        method: config.method || ''
                    });
                    
                    // For appointments, update the local IndexedDB immediately
                    if (entityType === 'appointment') {
                        await handleOfflineAppointmentChange(changeType, config.data, entityId);
                    }
                    
                    // Throw a custom error to prevent the actual API call
                    throw {
                        isOfflineError: true,
                        message: 'Request queued for processing when online',
                        data: config.data
                    };
                }
            } else {
                // For GET requests in offline mode, we'll let them proceed
                // They will likely be handled by the IndexedDB fallback in the services
                console.log('GET request in offline mode - proceeding to IndexedDB fallback');
            }
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle authentication errors
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        // If this is our custom offline error, handle it specially
        if (error.isOfflineError) {
            // Return a mock successful response
            return Promise.resolve({
                status: 200,
                statusText: 'OK (Offline)',
                data: error.data,
                headers: {},
                config: {},
                offline: true
            });
        }
        
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const token = await authService.refreshToken();
                
                // Update the failed request with the new token
                originalRequest.headers.Authorization = `Bearer ${token}`;
                
                // Retry the original request
                return apiClient(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                authService.logout();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Extract the entity type from the URL
 */
function getEntityTypeFromUrl(url: string): string | null {
    if (url.includes('/appointments')) return 'appointment';
    if (url.includes('/appointment-types')) return 'appointmentType';
    if (url.includes('/businesses')) return 'business';
    if (url.includes('/users')) return 'user';
    return null;
}

/**
 * Get the change type based on the HTTP method
 */
function getChangeTypeFromMethod(method: string): ChangeType | null {
    switch (method.toLowerCase()) {
        case 'post': return ChangeType.CREATE;
        case 'put': return ChangeType.UPDATE;
        case 'delete': return ChangeType.DELETE;
        default: return null;
    }
}

/**
 * Extract the entity ID from the URL
 */
function getEntityIdFromUrl(url: string, method: string): string | null {
    // For POST requests (create), there's no ID yet
    if (method.toLowerCase() === 'post') return null;
    
    // For PUT and DELETE, extract the ID from the URL
    const parts = url.split('/');
    return parts[parts.length - 1];
}

/**
 * Handle offline appointment changes by updating IndexedDB
 */
async function handleOfflineAppointmentChange(
    changeType: ChangeType, 
    data: any, 
    entityId: string | null
): Promise<void> {
    try {
        switch (changeType) {
            case ChangeType.CREATE:
                // For create, we need to generate a temporary ID
                const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                const newAppointment = {
                    ...data,
                    id: tempId,
                    createdAt: new Date().toISOString()
                };
                await indexedDbService.updateAppointment(newAppointment);
                break;
                
            case ChangeType.UPDATE:
                if (entityId) {
                    // Get the existing appointment
                    const existingAppointment = await indexedDbService.getAppointment(entityId);
                    if (existingAppointment) {
                        // Update it with the new data
                        const updatedAppointment = {
                            ...existingAppointment,
                            ...data,
                            id: entityId
                        };
                        await indexedDbService.updateAppointment(updatedAppointment);
                    }
                }
                break;
                
            case ChangeType.DELETE:
                if (entityId) {
                    await indexedDbService.deleteAppointment(entityId);
                }
                break;
        }
    } catch (error) {
        console.error('Error handling offline appointment change:', error);
    }
} 